import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  CheckCircle2, Circle, ArrowRight, Clock, Package, Wallet,
  Store, Upload, ImageIcon, Sparkles, AlertCircle, Phone,
} from "lucide-react";

const HORAS = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, "0")}:00`);

export default function MiTiendaTab({ tiendaId, onGoTo }) {
  const [data, setData] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [horariosOpen, setHorariosOpen] = useState(false);

  const load = () => api.get(`/tiendas/${tiendaId}/setup-status`).then(r => setData(r.data)).catch(e => toast.error(formatApiError(e)));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tiendaId]);

  if (!data) return <div className="p-8 text-zinc-500">Cargando…</div>;

  const { tienda, pasos, completados, total, porcentaje } = data;

  const iconMap = { productos: Package, horarios: Clock, inventario: Store, envios: Wallet };
  const goTo = {
    productos: () => onGoTo("inventario"),
    horarios: () => setHorariosOpen(true),
    inventario: () => onGoTo("inventario"),
    envios: () => onGoTo("envios"),
  };

  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-6">
      <div className="space-y-6">
        {/* Progress */}
        <div className="bg-white border border-zinc-200 rounded-sm p-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="overline text-zinc-500 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Guía de inicio
              </div>
              <div className="font-display font-black text-3xl tracking-tighter mt-1">
                {completados === total
                  ? "¡Todo listo para vender! 🎉"
                  : `Tu tienda está ${porcentaje}% lista`}
              </div>
              <p className="text-sm text-zinc-600 mt-2 max-w-md">
                {completados === total
                  ? "Tu local ya aparece en el catálogo del mall. Empieza a recibir pedidos."
                  : "Completa los 4 pasos para aparecer en el catálogo del mall y recibir pedidos online."}
              </p>
            </div>
            <div className="text-right">
              <div className="font-display font-black text-4xl tracking-tighter">{completados}/{total}</div>
              <div className="text-xs text-zinc-500 uppercase tracking-wider">completos</div>
            </div>
          </div>
          <div className="mt-5 h-2 bg-zinc-100 rounded-sm overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#FF3366] to-[#D4FF00] transition-all duration-500" style={{ width: `${porcentaje}%` }} />
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-3">
          {pasos.map((paso, i) => {
            const Icon = iconMap[paso.id] || Circle;
            return (
              <div
                key={paso.id}
                data-testid={`step-${paso.id}`}
                className={`bg-white border rounded-sm p-5 transition-colors ${
                  paso.completo ? "border-emerald-200 bg-emerald-50/30" : "border-zinc-200"
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* Step number + icon */}
                  <div className="shrink-0">
                    <div className={`w-11 h-11 rounded-sm grid place-items-center ${
                      paso.completo ? "bg-emerald-500 text-white" : "bg-zinc-900 text-white"
                    }`}>
                      {paso.completo ? <CheckCircle2 className="w-5 h-5" /> : <span className="font-display font-black text-lg">{i + 1}</span>}
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Icon className="w-4 h-4 text-zinc-500" />
                      <div className="font-display font-bold text-lg tracking-tight">{paso.titulo}</div>
                      {paso.completo && <span className="text-xs bg-emerald-100 text-emerald-700 rounded-sm px-2 py-0.5 font-medium">Completo</span>}
                    </div>
                    <p className="text-sm text-zinc-600 mt-1 leading-relaxed">{paso.descripcion}</p>

                    {paso.detalles && (
                      <div className="mt-2 text-xs text-zinc-500">{paso.detalles}</div>
                    )}

                    {paso.warnings && paso.warnings.length > 0 && (
                      <div className="mt-3 flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 rounded-sm p-2.5 text-amber-800">
                        <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        <div>
                          {paso.warnings.map((w, j) => <div key={`${paso.id}-warn-${j}`}>{w}</div>)}
                        </div>
                      </div>
                    )}
                  </div>

                  <Button
                    data-testid={`step-${paso.id}-cta`}
                    onClick={goTo[paso.id]}
                    size="sm"
                    className={`shrink-0 rounded-sm ${
                      paso.completo ? "bg-zinc-100 hover:bg-zinc-200 text-zinc-900" : "bg-[#FF3366] hover:bg-[#E62958] text-white"
                    }`}
                  >
                    {paso.completo ? "Editar" : paso.cta} <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Full walkthrough */}
        <button type="button" data-testid="mitienda-cuponeras-cta" onClick={() => onGoTo("cuponeras")} className="w-full text-left bg-[#FF3366] text-white rounded-sm p-5 hover:bg-[#E62958] transition-colors">
          <div className="overline text-white/70">Nuevo · Marketing</div>
          <div className="font-display font-black text-2xl tracking-tighter mt-1">Haz marketing con cupones 🎟️</div>
          <p className="text-sm text-white/85 mt-1">Diseña cupones con IA, imprime tu QR y tus clientes reciben descuentos en su celular. Hasta 5 cupones cada 30 días.</p>
        </button>
        <div className="bg-zinc-900 text-white rounded-sm p-6">
          <div className="overline text-white/50">Guía paso a paso</div>
          <div className="mt-2 font-display font-black text-2xl tracking-tighter">¿Cómo funciona en el día a día?</div>
          <ol className="mt-4 space-y-3 text-sm">
            {[
              "Un cliente entra al e-commerce general (máx. 5 artículos tuyos) o a tu tienda online (hasta 30 artículos).",
              "Agrega productos tuyos y de otras tiendas a un mismo carrito y paga una sola vez a la plataforma (Marketing de Inteligencia Artificial S de RL de CV).",
              "Recibes la orden en tu Kanban 'Pedidos e-Commerce'. Empacas y presionas 'Marcar como Empacado'.",
              "Entrega en 90 min: cuando TODAS las tiendas del pedido están listas, un repartidor acepta la ruta. Uber o foráneo: tú gestionas el envío desde 'Envíos y Foráneos'.",
              "El repartidor pasa a recoger a cada local. Ustedes le entregan con el token de verificación.",
              "El repartidor lleva todo al cliente y confirma con un código de 4 dígitos. ¡Venta cerrada!",
            ].map((t, i) => (
              <li key={`walkthrough-${i}`} className="flex gap-3">
                <span className="font-display font-black text-[#D4FF00] shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-white/80">{t}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>

      {/* Sidebar: profile card */}
      <div className="space-y-4">
        <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
          <div className="aspect-[16/10] bg-zinc-100 relative">
            {tienda.imagen_url ? (
              <img src={tienda.imagen_url} alt={tienda.nombre_local} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full grid place-items-center">
                <ImageIcon className="w-12 h-12 text-zinc-300" />
              </div>
            )}
            <button
              data-testid="edit-perfil"
              onClick={() => setEditOpen(true)}
              className="absolute bottom-3 right-3 bg-white rounded-sm px-3 py-1.5 text-xs font-medium shadow hover:bg-zinc-100"
            >
              Editar perfil
            </button>
          </div>
          <div className="p-5">
            <div className="overline text-zinc-500">Mi tienda</div>
            <div className="font-display font-black text-xl tracking-tighter mt-1">{tienda.nombre_local}</div>
            <div className="text-xs text-zinc-500 mt-0.5">Local {tienda.numero_local} · {tienda.categoria}</div>
            {tienda.descripcion && <p className="text-sm text-zinc-700 mt-3 leading-relaxed">{tienda.descripcion}</p>}
            {(tienda.horario_apertura || tienda.horario_cierre) && (
              <div className="mt-3 text-xs text-zinc-600 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" /> {tienda.horario_apertura || "—"} — {tienda.horario_cierre || "—"}
                {tienda.dias_operacion && <span>· {tienda.dias_operacion}</span>}
              </div>
            )}
            {tienda.telefono && (
              <div className="mt-1.5 text-xs text-zinc-600 flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5" /> {tienda.telefono}
              </div>
            )}
          </div>
        </div>

        <div className="rounded-sm bg-[#D4FF00] p-5">
          <div className="font-display font-black text-lg tracking-tight">💡 Consejo</div>
          <p className="text-sm mt-2 leading-relaxed">
            Sube <b>fotos verticales</b> de tus productos con fondo limpio. Los productos con imagen se venden hasta 3× más que los que no tienen.
          </p>
        </div>
      </div>

      <EditarPerfilDialog open={editOpen} onOpenChange={setEditOpen} tienda={tienda} onSaved={() => { load(); setEditOpen(false); }} tiendaId={tiendaId} />
      <HorariosDialog open={horariosOpen} onOpenChange={setHorariosOpen} tienda={tienda} onSaved={() => { load(); setHorariosOpen(false); }} tiendaId={tiendaId} />
    </div>
  );
}

function EditarPerfilDialog({ open, onOpenChange, tienda, onSaved, tiendaId }) {
  const [f, setF] = useState({});
  useEffect(() => {
    if (open) setF({
      nombre_local: tienda.nombre_local || "",
      categoria: tienda.categoria || "",
      descripcion: tienda.descripcion || "",
      telefono: tienda.telefono || "",
      imagen_url: tienda.imagen_url || "",
      whatsapp_gerente: tienda.whatsapp_gerente || "",
      direccion: tienda.direccion || "",
      colonia: tienda.colonia || "",
      ciudad: tienda.ciudad || "",
      estado: tienda.estado || "",
      codigo_postal: tienda.codigo_postal || "",
    });
  }, [open, tienda]);

  const set = (k, v) => setF(x => ({ ...x, [k]: v }));

  const uploadImage = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 500 * 1024) { toast.error("Máx 500KB"); return; }
    const reader = new FileReader();
    reader.onload = () => set("imagen_url", reader.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    try {
      await api.patch(`/tiendas/${tiendaId}/perfil`, f);
      toast.success("Perfil actualizado");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-lg">
        <DialogHeader><DialogTitle>Editar perfil de tienda</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Nombre del local</Label>
            <Input data-testid="perfil-nombre" value={f.nombre_local || ""} onChange={e => set("nombre_local", e.target.value)} className="mt-1 rounded-sm h-11" />
          </div>
          <div>
            <Label>Categoría</Label>
            <Input value={f.categoria || ""} onChange={e => set("categoria", e.target.value)} className="mt-1 rounded-sm h-11" placeholder="Moda, Tech, Comida..." />
          </div>
          <div>
            <Label>Descripción</Label>
            <Textarea data-testid="perfil-desc" value={f.descripcion || ""} onChange={e => set("descripcion", e.target.value)} rows={3} className="mt-1 rounded-sm" placeholder="Cuenta a tus clientes qué te hace especial…" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Teléfono</Label>
              <Input value={f.telefono || ""} onChange={e => set("telefono", e.target.value)} className="mt-1 rounded-sm h-11" placeholder="+52 55…" />
            </div>
            <div>
              <Label>WhatsApp gerente</Label>
              <Input data-testid="perfil-whatsapp" value={f.whatsapp_gerente || ""} onChange={e => set("whatsapp_gerente", e.target.value)} className="mt-1 rounded-sm h-11" placeholder="+52 55…" />
            </div>
          </div>
          {/* Ubicación Global */}
          <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3 space-y-2">
            <div className="overline text-zinc-500">Ubicación</div>
            <Input data-testid="perfil-direccion" value={f.direccion || ""} onChange={e => set("direccion", e.target.value)} placeholder="Dirección" className="rounded-sm h-10 text-sm" />
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="perfil-colonia" value={f.colonia || ""} onChange={e => set("colonia", e.target.value)} placeholder="Colonia" className="rounded-sm h-10 text-sm" />
              <Input data-testid="perfil-cp" value={f.codigo_postal || ""} onChange={e => set("codigo_postal", e.target.value)} placeholder="CP" maxLength={5} className="rounded-sm h-10 text-sm" />
              <Input data-testid="perfil-ciudad" value={f.ciudad || ""} onChange={e => set("ciudad", e.target.value)} placeholder="Ciudad" className="rounded-sm h-10 text-sm" />
              <Input data-testid="perfil-estado" value={f.estado || ""} onChange={e => set("estado", e.target.value)} placeholder="Estado" className="rounded-sm h-10 text-sm" />
            </div>
          </div>
          <div>
            <Label>Imagen de portada</Label>
            <div className="mt-1 flex items-center gap-3">
              <div className="w-24 h-16 border border-zinc-200 rounded-sm grid place-items-center bg-zinc-50 overflow-hidden">
                {f.imagen_url ? <img src={f.imagen_url} alt="preview" className="max-w-full max-h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-zinc-300" />}
              </div>
              <label className="cursor-pointer">
                <input data-testid="perfil-img" type="file" accept="image/*" onChange={uploadImage} className="hidden" />
                <span className="inline-flex items-center gap-1.5 px-3 h-9 rounded-sm border border-zinc-300 hover:border-zinc-900 text-sm font-medium">
                  <Upload className="w-3.5 h-3.5" /> Subir imagen
                </span>
              </label>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="perfil-save" onClick={save} className="bg-[#FF3366] hover:bg-[#E62958]">Guardar cambios</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HorariosDialog({ open, onOpenChange, tienda, onSaved, tiendaId }) {
  const [f, setF] = useState({});
  useEffect(() => {
    if (open) setF({
      horario_apertura: tienda.horario_apertura || "10:00",
      horario_cierre: tienda.horario_cierre || "21:00",
      dias_operacion: tienda.dias_operacion || "L-D",
    });
  }, [open, tienda]);

  const save = async () => {
    try {
      await api.patch(`/tiendas/${tiendaId}/perfil`, f);
      toast.success("Horarios guardados");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-md">
        <DialogHeader><DialogTitle>Configurar horarios</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Apertura</Label>
              <Select value={f.horario_apertura} onValueChange={v => setF(x => ({ ...x, horario_apertura: v }))}>
                <SelectTrigger data-testid="hor-apertura" className="mt-1 rounded-sm h-11"><SelectValue /></SelectTrigger>
                <SelectContent>{HORAS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Cierre</Label>
              <Select value={f.horario_cierre} onValueChange={v => setF(x => ({ ...x, horario_cierre: v }))}>
                <SelectTrigger data-testid="hor-cierre" className="mt-1 rounded-sm h-11"><SelectValue /></SelectTrigger>
                <SelectContent>{HORAS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Días de operación</Label>
            <Select value={f.dias_operacion} onValueChange={v => setF(x => ({ ...x, dias_operacion: v }))}>
              <SelectTrigger data-testid="hor-dias" className="mt-1 rounded-sm h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="L-D">Todos los días (L-D)</SelectItem>
                <SelectItem value="L-V">Lunes a Viernes</SelectItem>
                <SelectItem value="L-S">Lunes a Sábado</SelectItem>
                <SelectItem value="V-D">Viernes a Domingo</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="hor-save" onClick={save} className="bg-[#FF3366] hover:bg-[#E62958]">Guardar horarios</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
